import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Switch,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import contractService from "../../services/contract";
import userService from "../../services/users";
import queryDefault from "../../utils/queryDefault";
import Cookies from "js-cookie";

// Employee interface for dropdown
interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

interface ContractFormValues {
  name: string;
  pob: string; // place of birth
  dob: string; // date of birth
  last_education: string;
  gender: string;
  religion: string;
  address: string;
  id_number: string;
  email: string;
  phone: string;
  branch: string;
  branch_address: string;
  placement_location: string;
  salary: string;
  salary_type: string;
  allowance_health: string;
  allowance_operational: string
  employee_id: string;
  officer_id: string;
  contract_start: string;
  contract_end: string;
  is_probation?: boolean;
  project_id: string;
  username: string;
}

export default function ContractCreate() {
  const notification = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const queryParams = new URLSearchParams(location.search);
  const employeeIdFromUrl = queryParams.get("employee_id");

  const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    pob: yup.string().required("Place of birth is required"),
    dob: yup.string().required("Date of birth is required"),
    last_education: yup.string().required("Last education is required"),
    gender: yup.string().required("Gender is required"),
    religion: yup.string().required("Religion is required"),
    address: yup.string().required("Address is required"),
    id_number: yup.string().required("ID number is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().required("Phone is required"),
    branch: yup.string().required("Branch is required"),
    branch_address: yup.string().required("Branch address is required"),
    placement_location: yup.string().required("Placement location is required"),
    salary: yup.string().required("Salary is required"),
    salary_type: yup.string().required("Salary type is required"),
    allowance_health: yup.string(),
    allowance_operational: yup.string(),
    contract_start: yup.string().required("Contract start date is required"),
    contract_end: yup.string().required("Contract end date is required"),
  });

  const form = useForm<ContractFormValues>({
    defaultValues: {
      name: "",
      pob: "",
      dob: "",
      last_education: "",
      gender: "",
      religion: "",
      address: "",
      id_number: "",
      email: "",
      phone: "",
      branch: "",
      branch_address: "",
      placement_location: "",
      salary: "",
      salary_type: "monthly",
      employee_id: employeeIdFromUrl || "",
      officer_id: user.id || "",
      is_probation: false,
      contract_start: "",
      contract_end: "",
      project_id: user.project_id,
      username: user.username,
    },
    resolver: yupResolver(schema),
  });

  // watch is_probation so UI updates reactively when the switch changes
  const isProbation = form.watch("is_probation");

  // Fetch employee details if employeeIdFromUrl exists
  const { data: employeeData } = useQuery(
    ["employee", employeeIdFromUrl],
    () => userService.viewUser(employeeIdFromUrl),
    {
      ...queryDefault,
      enabled: !!employeeIdFromUrl,
      onSuccess: (data) => {
        if (data?.data) {
          // Prefill form with employee data if available
          form.setValue("name", data.data.name || "");
          form.setValue("email", data.data.email || "");
          form.setValue("phone", data.data.phone || "");
          if (data.data.id_number)
            form.setValue("id_number", data.data.id_number);
          if (data.data.address) form.setValue("address", data.data.address);
        }
      },
    }
  );

  const saveContract = useMutation(contractService.createContract, {
    onSuccess: () => {
      if (employeeIdFromUrl) {
        // If created from employee page, go back to employee detail
        navigate(`/app/dashboard/employee/${employeeIdFromUrl}`);
      } else {
        // Otherwise go to contract list
        navigate("/app/contract");
      }

      notification.onOpen({
        message: "Contract was successfully created!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);

  const fetchEmployees = async (query: string) => {
    setIsEmployeeLoading(true);
    try {
      const response = await userService.searchUsers(query);
      console.log(response)
      setEmployeeOptions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch employees", error);
    } finally {
      setIsEmployeeLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees("")
  }, [])


  const onSubmit = (values: ContractFormValues) => {
    saveContract.mutate(values);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Contract"
        breadcrumbs={
          employeeIdFromUrl
            ? [
                { label: "Dashboard", href: "/app/dashboard" },
                {
                  label: "Employee Details",
                  href: `/app/dashboard/employee/${employeeIdFromUrl}`,
                },
              ]
            : [{ label: "Contract Management", href: "/app/contract" }]
        }
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        {employeeIdFromUrl && employeeData?.data && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary">
              Creating contract for: {employeeData.data.name}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Employee</FormLabel>
                  <Controller
                    name="employee_id"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        id="employee-search"
                        options={employeeOptions}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.email})`
                        }
                        loading={isEmployeeLoading}
                        disabled={!!employeeIdFromUrl}
                        value={
                          employeeOptions.find(
                            (employee) => employee.id === field.value
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue ? newValue.id : "");
                          if (newValue) {
                            // Update other form fields with employee data
                            form.setValue("name", newValue.name);
                            form.setValue("email", newValue.email);

                            // Fetch full employee details to populate other fields
                            if (newValue.id) {
                              userService
                                .viewUser(newValue.id)
                                .then((response: any) => {
                                  const employeeData = response?.data;
                                  if (employeeData) {
                                    if (employeeData.phone)
                                      form.setValue(
                                        "phone",
                                        employeeData.phone
                                      );
                                    if (employeeData.id_number)
                                      form.setValue(
                                        "id_number",
                                        employeeData.id_number
                                      );
                                    if (employeeData.address)
                                      form.setValue(
                                        "address",
                                        employeeData.address
                                      );
                                  }
                                })
                                .catch((err) =>
                                  console.error(
                                    "Error fetching employee details",
                                    err
                                  )
                                );
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                            placeholder="Search employee by name or email"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isEmployeeLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Full Name</FormLabel>
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Place of Birth</FormLabel>
                  <Controller
                    name="pob"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Date of Birth</FormLabel>
                  <Controller
                    name="dob"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Last Education</FormLabel>
                  <Controller
                    name="last_education"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                      >
                        <MenuItem value="SD">SD</MenuItem>
                        <MenuItem value="SMP">SMP</MenuItem>
                        <MenuItem value="SMA">SMA</MenuItem>
                        <MenuItem value="D3">D3</MenuItem>
                        <MenuItem value="S1">S1</MenuItem>
                        <MenuItem value="S2">S2</MenuItem>
                        <MenuItem value="S3">S3</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.last_education && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.last_education.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Gender</FormLabel>
                  <Controller
                    name="gender"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                      >
                        <MenuItem value="Laki-Laki">Laki-Laki</MenuItem>
                        <MenuItem value="Perempuan">Perempuan</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.gender && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.gender.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Religion</FormLabel>
                  <Controller
                    name="religion"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                      >
                        <MenuItem value="Islam">Islam</MenuItem>
                        <MenuItem value="Kristen">Kristen</MenuItem>
                        <MenuItem value="Katolik">Katolik</MenuItem>
                        <MenuItem value="Hindu">Hindu</MenuItem>
                        <MenuItem value="Budha">Budha</MenuItem>
                        <MenuItem value="Lainnya">Lainnya</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.religion && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.religion.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>ID Number (KTP)</FormLabel>
                  <Controller
                    name="id_number"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Email</FormLabel>
                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="email"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Phone</FormLabel>
                  <Controller
                    name="phone"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Address</FormLabel>
                  <Controller
                    name="address"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        multiline
                        rows={4}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Employment Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Employment Information
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Branch</FormLabel>
                  <Controller
                    name="branch"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Branch Address</FormLabel>
                  <Controller
                    name="branch_address"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        multiline
                        rows={2}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Placement Location</FormLabel>
                  <Controller
                    name="placement_location"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Salary</FormLabel>
                  <Controller
                    name="salary"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Salary Type</FormLabel>
                  <Controller
                    name="salary_type"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.salary_type && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.salary_type.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Allowance Health</FormLabel>
                  <Controller
                    name="allowance_health"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                        placeholder="Additional allowance amount (if any)"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Allowance Operational</FormLabel>
                  <Controller
                    name="allowance_operational"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                        placeholder="Additional allowance amount (if any)"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Contract Period */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {isProbation ? "Probation Period" : "Contract Period"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl sx={{ mt: 1 }}>
                  <Controller
                    name="is_probation"
                    control={form.control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Is probation"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>{isProbation ? "Probation Start Date" : "Contract Start Date"}</FormLabel>
                  <Controller
                    name="contract_start"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>{isProbation ? "Probation End Date" : "Contract End Date"}</FormLabel>
                  <Controller
                    name="contract_end"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  employeeIdFromUrl
                    ? navigate(`/app/dashboard/employee/${employeeIdFromUrl}`)
                    : navigate("/app/contract")
                }
              >
                Cancel
              </Button>
              <LoadingButton
                loading={saveContract.isLoading}
                type="submit"
                variant="contained"
              >
                Save
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
